'use client';
import React, { useEffect, useState } from 'react';
import {
  Button,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Grow,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { EventBus } from '@/shared/utils';
import { z } from 'zod';
import { Auth as AuthDict } from '@/shared/i18n';
import { AuthEventKeys, AuthEvents } from '../events';
import { useAuthStore } from '../stores/auth-store';

type LoginFormProps = {
  eventBus: EventBus<AuthEvents>;
  dict: AuthDict;
};

export default function OidcLoginForm({ eventBus, dict }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const { user, hydrated } = useAuthStore();

  useEffect(() => {
    if (hydrated && user?.token) {
      setIsLoading(true);
      eventBus.emit(AuthEventKeys.oidcLogin.do, { token: user.token });
    }
  }, [user, hydrated]);

  // Ascolta gli errori emessi dall'eventBus
  useEffect(() => {
    const handleError = (error: Error) => {
      setIsLoading(false);
      const errorCode = error.message; // Contiene ora il codice di errore

      setLoginError(errorCode); // Usa `errorMessage` per aggiornare lo stato
    };

    eventBus.on(AuthEventKeys.oidcLogin.error, handleError);
    return () => {
      eventBus.off(AuthEventKeys.oidcLogin.error, handleError);
    };
  }, [eventBus]);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const loginSchema = z.object({
      email: z.string().email({ message: dict.auth_errors?.invalid_email || 'Email non valida' }),
      password: z.string().min(8, { message: dict.auth_errors?.short_password }),
    });
    const result = loginSchema.safeParse({ email, password });
    if (!result.success) {
      const formErrors: { email?: string; password?: string } = {};
      result.error.errors.forEach((err) => {
        if (err.path.includes('email')) {
          formErrors.email = err.message;
        }
        if (err.path.includes('password')) {
          formErrors.password = err.message;
        }
      });
      setErrors(formErrors);
    } else {
      setErrors({});
      setLoginError(null);
      setIsLoading(true);
      eventBus.emit(AuthEventKeys.oidcLogin.do, { email, password });
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="px-6 py-8 md:px-16 md:py-12 rounded-xl bg-paper">
        <div className="flex flex-col items-center gap-2 mb-12 text-center">
          <Typography variant="mac-sans-h3" component="h1" color="textPrimary">
            {dict.login?.title || 'Accedi a Vibly'}
          </Typography>
          <Typography variant="body-500" component="p" color="textSecondary">
            {dict.login?.description || 'Accedi al tuo account'}
          </Typography>
        </div>
        <div className="mb-8">
          <div className="flex flex-col gap-5">
            <Grow
              in={!!loginError}
              mountOnEnter
              unmountOnExit
              style={{ transformOrigin: 'center center' }}
              timeout={500}
            >
              <Alert severity="error">{loginError}</Alert>
            </Grow>
            <TextField
              placeholder={dict.login?.username_placeholder || 'Il tuo indirizzo e-mail'}
              variant="outlined"
              size="medium"
              className="max-md:max-w-full"
              label={
                <Typography variant="s2">
                  {dict.login?.username_label || 'La tua e-mail'}
                </Typography>
              }
              value={email}
              onChange={(e) => setEmail(e.target.value?.toLowerCase())}
              error={!!errors.email}
              helperText={
                errors.email && (
                  <Typography variant="caption-500" color="error">
                    {errors.email}
                  </Typography>
                )
              }
            />
            <FormControl variant="outlined" error={!!errors.password}>
              <InputLabel htmlFor="outlined-adornment-password">
                <Typography variant="s2">{dict.login?.password_label || 'Password'}</Typography>
              </InputLabel>
              <OutlinedInput
                placeholder="La tua password"
                id="outlined-adornment-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label={dict.login?.password_label || 'Password'}
              />
              {errors.password && (
                <Typography variant="caption-500" color="error">
                  {errors.password}
                </Typography>
              )}
            </FormControl>
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <Button
            size="large"
            variant="contained"
            type="submit"
            className="w-full whitespace-nowrap"
            disabled={isLoading || !email || !password}
          >
            <Typography variant="button-l">
              {isLoading || !hydrated ? (
                <CircularProgress size={14} />
              ) : (
                dict.login?.button_text || 'Accedi'
              )}
            </Typography>
          </Button>
        </div>
      </form>
    </>
  );
}
